/**
 * useFeedStaleness — tracks data freshness across all streams
 *
 * Uses ReconnectState from reconnect.ts to classify feed age as
 * fresh (<15s), stale (15-60s), or disconnected (>60s).
 *
 * The hook exposes:
 * - staleness level (for UI color coding)
 * - seconds since last update (for display timer)
 * - a recordMessage() callback for streams to call on each update
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { computeStaleness, type StalenessLevel } from '@/lib/reconnect';

export interface FeedStalenessReturn {
  /** Current staleness classification */
  staleness: StalenessLevel;
  /** Seconds since last message (null if never received) */
  secondsSinceUpdate: number | null;
  /** Call this whenever a stream message arrives */
  recordMessage: () => void;
  /** Raw timestamp of last message */
  lastMessageMs: number | null;
}

const TICK_MS = 1000;

export function useFeedStaleness(): FeedStalenessReturn {
  const lastMessageMsRef = useRef<number | null>(null);
  const [staleness, setStaleness] = useState<StalenessLevel>('disconnected');
  const [secondsSinceUpdate, setSecondsSinceUpdate] = useState<number | null>(null);

  const recordMessage = useCallback(() => {
    lastMessageMsRef.current = Date.now();
  }, []);

  // Tick every second to re-evaluate staleness
  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const lastMs = lastMessageMsRef.current;

      const level = lastMs === null
        ? 'disconnected'
        : computeStaleness(lastMs, now);

      setStaleness(level);
      setSecondsSinceUpdate(lastMs === null ? null : Math.floor((now - lastMs) / 1000));
    };

    tick(); // immediate first evaluation
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, []);

  return {
    staleness,
    secondsSinceUpdate,
    recordMessage,
    lastMessageMs: lastMessageMsRef.current,
  };
}
